/* eslint-disable array-callback-return */
import moment from 'moment/moment';
import momentDurationFormatSetup from 'moment-duration-format';
import { common } from 'themes/default';
import { compose, groupBy, mergeRight, subtract, sum } from 'ramda';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { filterSummatedFareTrains } from 'utils/summatedFares';
import productTypes from 'constants/productTypes';
import { PROMO_TYPE_DISCOUNT } from 'constants/promo';
import {
  CLASSES_OF_SERVICE_BY_TYPE,
  CLASSES_OF_SERVICE_BY_TYPE_SNCB,
  CARRIERS_WITH_TWO_CLASSES_OF_SERVICES,
  LYRIA1,
  LYRIA2,
  LYRIABP,
  LYRIA_SUMMATED_FARES_COS_PRIORITY_LIST,
  PLUS,
  PREMIER,
  SNCF2,
  SNCF1SP,
  SNCF1BP,
  SNCF_SUMMATED_FARES_COS_PRIORITY_LIST,
  PLUS_SNCF_SUMMATED_FARES_COS_PRIORITY_LIST,
  STANDARD,
  WC_COMP,
  EVAC,
  UNASSIGNED,
  SNCF_OPTIMUM,
  SNCF_OPTIMUMPLUS,
  SNCB1,
  NS1,
  NS2
} from 'constants/classOfService';
import { brandCode } from 'constants/brandCodes';

momentDurationFormatSetup(moment);

export const getUniqueClassesByCode = ({ classesOfService = [] }) =>
  classesOfService.reduce((classServices, current) => {
    const classCodes = classServices.find(({ code }) => code === current.code);
    return !classCodes ? [...classServices, current] : classServices;
  }, []);

// Only for single exchanges for now
// as single legs outside of ES / ER cannot be booked on their own but can be exchanged
// SNCB and NS use two fare columns for individual-leg exchanges.
export const showTwoClassesOfServicesForCarrierBeingExchanged = ({ legsBeingExchanged = [] }) => {
  const isSingleLegExchange = legsBeingExchanged.length === 1;
  const isCarrierWithTwoClassesOfServices = CARRIERS_WITH_TWO_CLASSES_OF_SERVICES.includes(legsBeingExchanged[0]?.serviceType?.code);

  return isSingleLegExchange && isCarrierWithTwoClassesOfServices;
};

export const filterJourneys = (
  isExchange,
  isMultiJourneyBooking,
  summatedFaresConditions,
  exchanginASubsetOfPaxs,
  filterEROnly,
  journeys,
  isSummatedFaresSelectionEnabled,
  isExchangingAllLegsOfBooking,
  classesOfService,
  filteredProductFamily,
  legBeingExchanged
) => {
  if (isExchange) {
    const journeysFilteredBasedOnServiceTypesExchanged = filterAvailableExchangeJourneysBasedOnServiceType({
      legBeingExchanged,
      exchangeResults: journeys
    });
    if (isMultiJourneyBooking) {
      return journeysFilteredBasedOnServiceTypesExchanged?.filter(filterEROnly);
    }

    if (exchanginASubsetOfPaxs && isSummatedFaresSelectionEnabled) {
      return summatedFaresConditions
        ? filterSummatedFareTrains(
            journeysFilteredBasedOnServiceTypesExchanged,
            summatedFaresConditions,
            isExchangingAllLegsOfBooking,
            exchanginASubsetOfPaxs
          )
        : journeysFilteredBasedOnServiceTypesExchanged;
    }

    if (!!summatedFaresConditions && isSummatedFaresSelectionEnabled) {
      return filterSummatedFareTrains(
        journeysFilteredBasedOnServiceTypesExchanged,
        summatedFaresConditions,
        isExchangingAllLegsOfBooking,
        exchanginASubsetOfPaxs
      );
    }

    return journeysFilteredBasedOnServiceTypesExchanged;
  }

  if (filteredProductFamily) {
    return journeys.map(({ fares, ...journey }) => {
      const filteredFares = [];

      classesOfService.forEach(({ code }) => {
        const faresByCoS = fares.filter(({ classOfService }) => classOfService?.code === code);
        const faresForProductFamily = faresByCoS.filter(
          ({ productFamily }) => productFamily?.familyGroup === filteredProductFamily || productFamily?.code === filteredProductFamily
        );

        if (faresForProductFamily.length > 0) {
          filteredFares.push(...faresForProductFamily);
        } else {
          filteredFares.push({
            ...faresByCoS[0],
            bookingItems: [],
            buckets: [],
            fees: [],
            prices: {
              displayPrice: null
            }
          });
        }
      });

      return {
        ...journey,
        fares: filteredFares
      };
    });
  }

  return journeys;
};

export const groupByTimeOfDay = compose(
  mergeRight({ morningJourneys: [], afternoonJourneys: [] }),
  groupBy((journey) =>
    moment(journey.timing.departs, 'HH:mm').isBefore(moment('12:00', 'HH:mm')) ? 'morningJourneys' : 'afternoonJourneys'
  )
);

export const shouldDisplayMultipleFares = (fare, journey) => {
  const classCodeSelected = fare?.classOfService?.code;
  const getFaresForClassCodeSelected = journey?.fares?.filter((journeyFare) => journeyFare?.classOfService?.code === classCodeSelected);
  const isSingleLegFirstClassExchange = getFaresForClassCodeSelected.every((fare) => [SNCB1, NS1].includes(fare?.classOfService?.code));
  return getFaresForClassCodeSelected?.length > 1 && !isSingleLegFirstClassExchange;
};

export const getDisplayPrice = ({
  isGroupBooking,
  showFareDifference,
  totalPrice,
  fareData,
  originalTotalPrice,
  isExchange,
  isFlowForward,
  shouldOverridePrice
}) => {
  if (isFlowForward) {
    return fareData.prices.displayPrice * fareData.bookingItems?.length;
  }
  if (!isExchange) {
    return showFareDifference ? Math.max(0, subtract(totalPrice, originalTotalPrice)) : totalPrice;
  }

  if (shouldOverridePrice && isExchange) {
    return 0;
  }

  if (!isGroupBooking && isExchange) {
    return totalPrice;
  }

  return fareData?.prices?.displayPrice;
};

export const buildColumns = (
  currency,
  lowestFares = {},
  classesOfService = [],
  isWheelchairSearch = false,
  isEvacSearch = false,
  isUnassignedSearch = false,
  isLoyaltyBooking = false,
  shouldDisplayTwoClassesOfService = false,
  classOfServiceToExchange = [],
  legsBeingExchanged = []
) => {
  const colors = common.searchResults;
  const columns = [
    {
      key: 'departAt',
      width: 2,
      columnBackgroundColor: colors.header.default.backgroundColour,
      columnColor: colors.header.default.fontColour,
      horizontalAlignment: 'center'
    },
    {
      key: 'duration',
      width: 2,
      columnBackgroundColor: colors.header.default.backgroundColour,
      columnColor: colors.header.default.fontColour,
      horizontalAlignment: 'center'
    },
    {
      key: 'arriveAt',
      width: 2,
      columnBackgroundColor: colors.header.default.backgroundColour,
      columnColor: colors.header.default.fontColour,
      horizontalAlignment: 'center'
    }
  ];

  let fareClasses = [];

  if (shouldDisplayTwoClassesOfService) {
    // Single leg exchanges only
    const carriers = legsBeingExchanged[0].serviceType?.code;
    const uniqueClasses = getUniqueClassesByCode({ classesOfService });

    if (carriers === serviceTypeCodes.SNCB) {
      fareClasses = uniqueClasses.filter(({ code }) =>
        [...CLASSES_OF_SERVICE_BY_TYPE_SNCB[STANDARD], ...CLASSES_OF_SERVICE_BY_TYPE_SNCB[PREMIER]].includes(code)
      );
    } else if (carriers === serviceTypeCodes.NS) {
      fareClasses = uniqueClasses.filter(({ code }) => [NS2, NS1].includes(code));
    } else {
      fareClasses = classesOfService.filter(
        ({ code }) =>
          !CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code) &&
          !CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code) &&
          !CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code)
      );
    }
  } else if (isWheelchairSearch) {
    fareClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code));
  } else if (isEvacSearch) {
    fareClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code));
  } else if (isUnassignedSearch) {
    fareClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code));
  } else {
    fareClasses = classesOfService.filter(
      ({ code }) =>
        !CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code) &&
        !CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code) &&
        !CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code)
    );
  }

  const classColumns = fareClasses.map(({ code, name }) => {
    const classColor = colors.header[code] ?? colors.header.default;

    return {
      key: code,
      name,
      width: 2,
      columnBackgroundColor: classColor.backgroundColor,
      columnColor: classColor.fontColour,
      horizontalAlignment: 'center',
      currency,
      isLoyaltyBooking,
      lowestFare: lowestFares[code]
    };
  });

  columns.push(...classColumns);

  return columns;
};

export const getClassFare = (classCode, fares = [], exchangedClassesOfService, isExchangingSingleLegOfASummated) => {
  const allFares = fares.filter(({ classOfService } = {}) => classOfService?.code === classCode);
  // SNCB and NS have two classes but return three fare bundles, with first class
  // duplicated. Skip the empty bundle so the available first-class fare is used.
  const isSingleTwoClassCarrierExchange =
    isExchangingSingleLegOfASummated &&
    allFares.every((fares) => fares.legs?.every((leg) => CARRIERS_WITH_TWO_CLASSES_OF_SERVICES.includes(leg.serviceType?.code)));
  const filteredFares = isSingleTwoClassCarrierExchange ? allFares.filter((fares) => fares.bookingItems.length > 0) : allFares;
  const hasLyriaSummatedFare = filteredFares.length > 0 && filteredFares.every(({ isLyria, isSummatedFare }) => isLyria && isSummatedFare);
  const hasSNCFSummatedFare =
    filteredFares.length > 0 &&
    filteredFares.every(
      ({ legs, isSummatedFare }) => legs.some(({ serviceType }) => serviceType.brandCode === brandCode.SNCF) && isSummatedFare
    );

  const hasINOUISummatedFare =
    filteredFares.length > 0 &&
    filteredFares.every(
      ({ legs, isSummatedFare }) => legs.some(({ serviceType }) => serviceType.brandCode === brandCode.INOUI) && isSummatedFare
    );

  if (hasLyriaSummatedFare) {
    const lyriaPriorityList = getSummatedFarePriorityList({ currentClass: classCode, exchangedClassesOfService, isLyria: true });

    const groupedFaresByLyriaClassOfService = getGroupedFaresByClassOfService({ fares: filteredFares, isLyria: true });

    return lyriaPriorityList.flatMap((classOfService) => groupedFaresByLyriaClassOfService[classOfService] || [])[0];
  }

  if (hasSNCFSummatedFare || hasINOUISummatedFare) {
    const SNCFPriorityList = getSummatedFarePriorityList({
      currentClass: classCode,
      exchangedClassesOfService,
      isSNCF: hasSNCFSummatedFare,
      isInoui: hasINOUISummatedFare
    });

    const groupedFaresBySNCFClassOfService = getGroupedFaresByClassOfService({
      fares: filteredFares,
      isSNCF: hasSNCFSummatedFare,
      isInoui: hasINOUISummatedFare
    });

    return SNCFPriorityList.flatMap((classOfService) => groupedFaresBySNCFClassOfService[classOfService] || [])[0];
  }

  let classFare = filteredFares[0];

  if (!classFare) {
    const classesMapper = Object.values(CLASSES_OF_SERVICE_BY_TYPE).find((classes) => classes.includes(classCode));

    classFare = fares.find(({ classOfService }) => classesMapper.includes(classOfService?.code));
  }

  return classFare;
};

export const getLoyaltyPoints = (fareData) => {
  const [leg] = fareData.legs;

  if (!leg) {
    return 0;
  }

  return sum(leg?.products.filter((product) => product.loyaltyPoints !== null)?.map((product) => product.loyaltyPoints || 0) || []);
};

export const getAllFaresPerClass = (journeys, classOfService) =>
  journeys
    ?.map((journey) =>
      journey?.fares?.filter((fare) => {
        const mappedClasses = Object.values(CLASSES_OF_SERVICE_BY_TYPE).find((classes) => classes.includes(classOfService)) ?? [WC_COMP];

        return mappedClasses.includes(fare?.classOfService?.code);
      })
    )
    ?.flat();

export const getLowestFarePerClass = (allFaresPerClass) => {
  const lowestFarePerClass = allFaresPerClass?.map((af) => af?.prices?.displayPrice * af.bookingItems?.length)?.filter(Number.isFinite);

  return allFaresPerClass.length > 0 && allFaresPerClass?.some((f) => f?.canBeBooked)
    ? lowestFarePerClass.length
      ? Math.min(...lowestFarePerClass)
      : null
    : -1;
};

export const getLowestLoyaltyFaresPerClass = (allFaresPerClass) => {
  const lowestLoyaltyFarePerClass = allFaresPerClass
    ?.map((af) => sum(af?.bookingItems?.map((bi) => bi?.loyaltyPoints)))
    ?.flat()
    ?.filter(Number.isFinite);

  return allFaresPerClass.length > 0 && allFaresPerClass?.some((f) => f?.canBeBooked)
    ? lowestLoyaltyFarePerClass.length
      ? Math.min(...lowestLoyaltyFarePerClass)
      : null
    : -1;
};

export const getLowestTotalFaresPerClass = (allFaresPerClass, isGroup = false, isExchange = false) => {
  const lowestGroupFarePerClass =
    isExchange && isGroup
      ? allFaresPerClass?.map((af) => af?.prices?.displayPrice)?.filter(Number.isFinite)
      : allFaresPerClass
          ?.filter((af) => af?.bookingItems?.length > 0)
          .map((af) => sum(af?.bookingItems?.map((bi) => bi?.price) || []))
          ?.flat()
          .filter((f) => !isNaN(f));

  return allFaresPerClass.length > 0 && allFaresPerClass?.some((f) => f?.canBeBooked)
    ? lowestGroupFarePerClass.length
      ? Math.min(...lowestGroupFarePerClass)
      : null
    : -1;
};

export const getLowestLyriaSummatedFarePerClass = (allFaresPerClass = [], exchangedClassesOfService) => {
  const lyriaPriorityList = getSummatedFarePriorityList({
    currentClass: allFaresPerClass[0]?.classOfService?.code,
    exchangedClassesOfService,
    isLyria: true
  });

  const groupedFaresByLyriaClassOfService = getGroupedFaresByClassOfService({ fares: allFaresPerClass, isLyria: true });

  let lowestPrice = 0;

  for (let classOfService of lyriaPriorityList) {
    const fares = groupedFaresByLyriaClassOfService[classOfService];

    if (fares) {
      const farePrices = fares
        ?.map((fare) =>
          (fare?.legs ?? []).reduce((acc, leg) => {
            const productPrice = leg.products.reduce((acc, product) => acc + product.price, 0);

            return acc + productPrice;
          }, 0)
        )
        .filter(Number.isFinite);

      lowestPrice = Math.min(...farePrices);

      break;
    }
  }

  return lowestPrice;
};

export const getFaresPerClass = ({ journeys, classOfService, isExchange = false }) => {
  const getJourneys = isExchange
    ? journeys.filter((afc) => !afc?.isExistingTrain || !afc.isExistingTrain(getClassFare(classOfService, afc.fares), classOfService))
    : journeys;

  return getAllFaresPerClass(getJourneys, classOfService);
};

export const getLowestFarePrice = ({
  fares = [],
  isLoyalty = false,
  isFlowForward = false,
  isGroup = false,
  isExchange = false,
  exchangedClassesOfService
}) => {
  if (isLoyalty) {
    return getLowestLoyaltyFaresPerClass(fares);
  }
  if (isFlowForward) {
    return getLowestFarePerClass(fares, true);
  }

  const filteredFares = fares.filter(({ bookingItems } = {}) => bookingItems?.length);
  const hasLyriaSummatedFare = filteredFares.every(({ isLyria, isSummatedFare }) => isLyria && isSummatedFare);

  if (hasLyriaSummatedFare) {
    return getLowestLyriaSummatedFarePerClass(filteredFares, exchangedClassesOfService);
  }

  return getLowestTotalFaresPerClass(fares, isGroup, isExchange);
};

export const isGreenspeedTrain = (journeys) =>
  journeys?.some((j) => j?.fares?.some((f) => !!f?.legs?.find((l) => l?.serviceType?.code === serviceTypeCodes.GREENSPEED)));

export const isEurostarTrain = (journeys) =>
  journeys?.some((j) => j?.fares?.some((f) => !!f?.legs?.find((l) => l?.serviceType?.code === serviceTypeCodes.EUROSTAR)));

export const getNumberOfSeatsLeft = (fareData) => {
  const physicalAvailability = (fareData?.buckets && fareData?.buckets[0]?.physicalAvailability) || 0;
  const highestLogicalAvailability = (fareData?.buckets && fareData?.buckets[0]?.highestBucketCodeLogicalAvailability) || 0;

  return Math.min(physicalAvailability, highestLogicalAvailability);
};

export const isBetweenRange = (value, minValue, maxValue) => value >= minValue && value <= maxValue;

export const getPromoDiscount = (fare) => {
  if (fare?.promo?.type !== PROMO_TYPE_DISCOUNT) {
    return null;
  }

  return fare.promo.value || null;
};

export const isAvailableTrain = (fares, dogOnlySearch = false, animalsPerTrain = [], assistancePerTrain = []) =>
  animalsPerTrain.length > 0 ||
  assistancePerTrain.length > 0 ||
  fares?.some(
    ({ buckets }) =>
      buckets?.some(
        ({ availability, physicalAvailability, price }) => availability && physicalAvailability && (dogOnlySearch ? true : price)
      ) || false
  );

export const isExchangingNonWheelchairClassCarriersWithWheelchairs = ({ isExchange = false, legsBeingExchanged = [] }) => {
  if (!isExchange) return false;
  const nonWheelchairClassCarriers = [serviceTypeCodes.SNCB, serviceTypeCodes.DB];
  const isExchangingNonWheelchairClassWithMultipleLegs =
    legsBeingExchanged.length > 1 && legsBeingExchanged.some(({ serviceType }) => nonWheelchairClassCarriers.includes(serviceType?.code));

  if (!isExchangingNonWheelchairClassWithMultipleLegs) return false;
  return legsBeingExchanged.some(({ classOfService }) => CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(classOfService.code));
};

export const isClassOfAccomodationBookable = ({
  fareData = {},
  isExchange = false,
  isMember = false,
  isExchangingSingleSummatedFareLeg = false,
  classOfService,
  classOfServiceToExchange,
  legsBeingExchanged = []
}) => {
  if (!isExchange) return fareData?.canBeBooked;

  const isLoyaltyExchangeToBPAvailable =
    isExchange && fareData?.productFamily?.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY && isMember;

  const canBeBookedForSingleSummated = isExchangingSingleSummatedFareLeg ? classOfService === classOfServiceToExchange : true;
  if (isLoyaltyExchangeToBPAvailable)
    return (
      (fareData?.canLoyaltyFareBeExchangedToBP && canBeBookedForSingleSummated) ?? (fareData?.canBeBooked && canBeBookedForSingleSummated)
    );

  const exchangingNonWheelchairClassWithWheelchair = isExchangingNonWheelchairClassCarriersWithWheelchairs({
    isExchange,
    legsBeingExchanged
  });
  if (exchangingNonWheelchairClassWithWheelchair) return true;
  return fareData?.canBeBooked && canBeBookedForSingleSummated;
};

export const journeyIsIndirect = (isABSOrADS, journey) => isABSOrADS || journey.fares[0].legs.length > 1;

export const runValidator = (booking, selectedItemRefs) => (validators) => {
  const segments = [...getSegmentsForItemRefs(booking, selectedItemRefs)];

  return validators?.every((validator) => validator({ segments, selectedItemRefs, booking }));
};

const getSegmentsForItemRefs = ({ inbound, outbound }, itemRefs) => {
  const inboundSegments = filterLegsByProductRefs(inbound, itemRefs);
  const outboundSegments = filterLegsByProductRefs(outbound, itemRefs);

  return new Set([...inboundSegments, ...outboundSegments]);
};

export const filterLegsByProductRefs = (legs, productRefs) =>
  legs?.filter((leg) => (leg.bookingItems || leg.products)?.some((item) => productRefs?.includes(item.itemRef)));

export const allSegmentsCanBeRebooked = ({ segments, selectedItemRefs = [] }) => {
  const hasActiveAOP = segments?.some(({ activeBookingItems }) =>
    activeBookingItems
      ?.filter(({ itemRef }) => selectedItemRefs?.includes(itemRef))
      ?.some(
        ({ canBeRebooked, afterSalesCurrentOverrides }) =>
          afterSalesCurrentOverrides?.length > 0 &&
          afterSalesCurrentOverrides?.some((afterSalesCurrentOverride) => afterSalesCurrentOverride?.type === 'REBOOK') &&
          canBeRebooked
      )
  );

  if (hasActiveAOP) {
    return true;
  }

  return segments?.every(({ activeBookingItems }) => {
    if (activeBookingItems && activeBookingItems.length > 0) {
      return activeBookingItems?.every(({ canBeRebooked }) => canBeRebooked);
    }

    return false;
  });
};

export const allSegmentsCannotBeRebooked = ({ segments }) => !allSegmentsCanBeRebooked({ segments });

export const getGroupedFaresByClassOfService = ({ fares = [], isLyria = false, isSNCF = false, isInoui = false }) => {
  const brand = isLyria ? brandCode.Lyria : isSNCF ? brandCode.SNCF : isInoui ? brandCode.INOUI : null;

  if (!brand) return {};

  return fares.reduce((acc, fare) => {
    const brandFare = fare?.legs.find(({ serviceType }) => serviceType?.brandCode === brand);

    if (brandFare) {
      const classOfService = brandFare.classOfService.code;

      return {
        ...acc,
        [classOfService]: [...(acc[classOfService] || []), fare]
      };
    }

    return acc;
  }, {});
};

export const getSummatedFarePriorityList = ({
  currentClass,
  exchangedClassesOfService,
  isLyria = false,
  isSNCF = false,
  isInoui = false
}) => {
  let faresPriorityList;

  if (isLyria) {
    switch (`${exchangedClassesOfService?.[brandCode.Eurostar]}_${exchangedClassesOfService?.[brandCode.Lyria]}`) {
      case `${PLUS}_${LYRIA2}`:
      case `${PREMIER}_${LYRIA2}`:
        faresPriorityList = LYRIA_SUMMATED_FARES_COS_PRIORITY_LIST;

        break;
      case `${STANDARD}_${LYRIA1}`:
      case `${STANDARD}_${LYRIABP}`:
      case `${PLUS}_${LYRIA1}`:
      case `${PLUS}_${LYRIABP}`:
      case `${PREMIER}_${LYRIA1}`:
      case `${PREMIER}_${LYRIABP}`:
        faresPriorityList = LYRIA_SUMMATED_FARES_COS_PRIORITY_LIST.toReversed();

        break;
      default:
        faresPriorityList =
          currentClass !== STANDARD ? LYRIA_SUMMATED_FARES_COS_PRIORITY_LIST.toReversed() : LYRIA_SUMMATED_FARES_COS_PRIORITY_LIST;
    }
  }

  if (isSNCF || isInoui) {
    switch (
      `${exchangedClassesOfService?.[brandCode.Eurostar]}_${exchangedClassesOfService?.[isSNCF ? brandCode.SNCF : brandCode.INOUI]}`
    ) {
      case `${STANDARD}_${SNCF2}`:
      case `${STANDARD}_${SNCF1SP}`:
      case `${STANDARD}_${SNCF1BP}`:
        faresPriorityList = SNCF_SUMMATED_FARES_COS_PRIORITY_LIST;

        break;
      case `${PLUS}_${SNCF2}`:
      case `${PLUS}_${SNCF1SP}`:
      case `${PLUS}_${SNCF1BP}`:
        faresPriorityList = PLUS_SNCF_SUMMATED_FARES_COS_PRIORITY_LIST;

        break;
      case `${PREMIER}_${SNCF2}`:
      case `${PREMIER}_${SNCF1SP}`:
      case `${PREMIER}_${SNCF1BP}`:
      case `${PREMIER}_${SNCF_OPTIMUM}`:
      case `${PREMIER}_${SNCF_OPTIMUMPLUS}`:
        faresPriorityList = SNCF_SUMMATED_FARES_COS_PRIORITY_LIST.toReversed();

        break;
      default:
        faresPriorityList =
          currentClass === PREMIER
            ? SNCF_SUMMATED_FARES_COS_PRIORITY_LIST.toReversed()
            : currentClass === PLUS
            ? PLUS_SNCF_SUMMATED_FARES_COS_PRIORITY_LIST
            : SNCF_SUMMATED_FARES_COS_PRIORITY_LIST;
    }
  }

  return faresPriorityList;
};

export const addNumberOfStationsToJourney = ({ journeys, serviceDetailsData }) =>
  journeys.map((journey) => ({
    ...journey,
    numberOfStations:
      serviceDetailsData.find(({ trainNumber }) => trainNumber === journey?.fares?.[0]?.legs?.[0]?.trainNumber)?.numberOfStations || 0
  }));

export const sortBasedOnNumberOfStations = ({ journeysWithStations }) =>
  [...journeysWithStations].sort((a, b) => {
    if (a.timing?.departs === b.timing?.departs) {
      if (a.numberOfStations - b.numberOfStations < 0) return a.numberOfStations - b.numberOfStations;
    }
  });

export const passengersExchangedDoNotHaveExtraLuggages = ({ extras = [], legsBeingExchanged = [] }) => {
  const passengersBeingExchanged = legsBeingExchanged.flatMap(({ products }) => products)?.map(({ passengerId }) => passengerId);
  const luggageExtras = extras.filter(({ type, cancelled, provisional }) => type === productTypes.LUGGAGE && !cancelled && !provisional);
  const isExchangingExtras = luggageExtras?.length > 0 && passengersBeingExchanged?.length > 0;

  if (isExchangingExtras) return !luggageExtras?.some(({ passengerId }) => passengersBeingExchanged?.includes(passengerId));
  return false;
};

export const hideLowestFares = ({
  isExchange = false,
  isSummatedEnabled = false,
  isSingleSummated = false,
  isMultipleSummated = false,
  isThroughFare = false,
  legsBeingExchanged = []
}) =>
  isSummatedEnabled &&
  isExchange &&
  (isMultipleSummated ||
    isThroughFare ||
    (isSingleSummated && legsBeingExchanged.length === 1 && legsBeingExchanged[0].serviceType?.code === serviceTypeCodes.SNCF));

export const getRequiredLuggageByCode = (extras = []) =>
  extras
    .filter((extra) => extra.type === productTypes.LUGGAGE && !extra.cancelled && !extra.provisional)
    .reduce((acc, extra) => {
      const { code } = extra;
      if (code) {
        acc[code] = (acc[code] || 0) + 1;
      }
      return acc;
    }, {});

export const isLuggageSoldOut = ({ extras = [], fareData = {}, legsBeingExchanged = [] }) => {
  const requiredLuggage = getRequiredLuggageByCode(extras);
  const requiredLuggageCodes = Object.keys(requiredLuggage);
  const passengersExchangedWithoutLugagges = passengersExchangedDoNotHaveExtraLuggages({ extras, legsBeingExchanged });

  if (requiredLuggageCodes.length === 0 || passengersExchangedWithoutLugagges) {
    return false;
  }

  if (!fareData.legs || fareData.legs.length === 0) {
    return true;
  }

  return requiredLuggageCodes.some((productCode) => {
    const requiredCount = requiredLuggage[productCode];

    const availabilityPerLeg = fareData.legs.map((leg) => {
      const luggageProduct = (leg.addOnProducts || []).find((p) => p.code === productCode);

      if (!luggageProduct) {
        return 0;
      }

      return Math.floor((luggageProduct.physicalAvailability || 0) / (luggageProduct.inventoryUnits || 1));
    });

    const minAvailability = Math.min(...availabilityPerLeg);

    return minAvailability < requiredCount;
  });
};

export const filterAvailableExchangeJourneysBasedOnServiceType = ({ legBeingExchanged, exchangeResults }) => {
  if (!legBeingExchanged || legBeingExchanged?.length === 0) return exchangeResults;
  const serviceTypesBeingExchanged = legBeingExchanged.map((l) => l.serviceType?.code);
  // Exchanging with ER or ES
  if (serviceTypesBeingExchanged.includes(serviceTypeCodes.EUROSTAR) || serviceTypesBeingExchanged.includes(serviceTypeCodes.GREENSPEED)) {
    return exchangeResults.filter(
      (exchangeResult) =>
        !exchangeResult.fares?.every((fare) =>
          fare.legs?.every((leg) => leg.serviceType?.code === serviceTypeCodes.SNCF || leg.serviceType?.code === serviceTypeCodes.SNCB)
        )
    );
  }

  // Exchanging SNCF or SNCB only
  return exchangeResults.filter((exchangeResult) =>
    exchangeResult.fares?.every((fare) =>
      fare.legs?.every(
        (leg) => serviceTypesBeingExchanged.includes(leg.serviceType?.code) && serviceTypesBeingExchanged.length === fare.legs.length
      )
    )
  );
};

export const exchangingAllLegsOfBooking = ({ booking = {}, outboundLegsBeingExchanged = [], inboundLegsBeingExchanged = [] }) => {
  if (booking.outbound?.length > 0 && booking.inbound?.length > 0) {
    return (
      outboundLegsBeingExchanged.length === booking.outbound.filter(({ cancelled }) => !cancelled)?.length &&
      inboundLegsBeingExchanged.length === booking.inbound.filter(({ cancelled }) => !cancelled)?.length
    );
  }

  if (booking.inbound?.length > 0) {
    return inboundLegsBeingExchanged.length === booking.inbound.filter(({ cancelled }) => !cancelled)?.length;
  }

  return outboundLegsBeingExchanged.length === booking.outbound?.filter(({ cancelled }) => !cancelled)?.length;
};
